import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';

const ChildrenOfLeaProject = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [statementOpen, setStatementOpen] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const galleryImages = [
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/wa7on4cm_000029180012.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/7t0z3ae7_000029180011.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/0mm61x2s_000029180001-Edit.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/7lbdtlj7_000029190003.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/ssl3fkak_000044870009.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/ao9tvpce_000044880010.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/r53nu0rw_000097920010-2.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/kitheque_000033660014.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/bjqsjnab_579980040013.jpg',
  ];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="bg-cream min-h-screen">
      {/* Back Button */}
      <div className="fixed top-24 left-6 md:left-12 z-40">
        <Link 
          to="/#photography" 
          className="inline-flex items-center gap-2 text-green hover:text-green/70 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm uppercase tracking-wider">Back</span>
        </Link>
      </div>

      {/* Title */}
      <section className="pt-32 pb-8 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <h1 
            className="text-6xl md:text-8xl lg:text-9xl uppercase text-green mb-4"
            style={{ fontFamily: 'Anton, sans-serif' }}
          >
            Children of the Lea
          </h1>
          <p className="text-xl md:text-2xl text-green/70">
            Growing up on London's canals
          </p>
        </div>
      </section>

      {/* Artist Statement Toggle */}
      <section className="px-6 md:px-12 pb-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setStatementOpen(!statementOpen)}
            className="flex items-center gap-2 text-green hover:text-green/70 transition-colors mx-auto"
          >
            <span className="text-sm uppercase tracking-wider">Artist Statement</span>
            {statementOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {statementOpen && (
            <div className="mt-8 space-y-6 text-green/90" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              <p className="text-lg leading-relaxed">
                Remember before screens when our imaginations were all we had to build our playtime? When a patch of grass could become a kingdom, a cupboard a portal, a river an enchanted world of pure magic?
              </p>
              <p className="text-lg leading-relaxed">
                Life on a boat demands attention to the elements; it puts you close to nature, and to the places where imagination used to thrive.
              </p>
              <p className="text-lg leading-relaxed">
                On the River Lea, families live afloat in ways that feel both ancient and fragile. Rising mooring fees, overstretched facilities, and tightening regulations are putting pressure on boat-dwellers.
              </p>
              <p className="text-lg leading-relaxed">
                Within this shifting reality, children on the river still build entire universes from branches, mud, reeds, and stories older than any screen. Their boats become castles, islands, pirate ships; towpaths become forests; embankments become secret realms.
              </p>
              <p className="text-lg leading-relaxed">
                Together, we recreate these magical landscapes - drawing from myths, old tales, and the wildness of imagination untouched by the digital world.
              </p>
              <p className="text-lg leading-relaxed">
                This project explores the magic of childhood through the eyes of those growing up on the water, and the intertwined identities of families whose lives are shaped by continual movement.
              </p>
              <p className="text-lg leading-relaxed">
                It asks how water influences who we become, how we play, how we cope, and how we stay connected in a world that is increasingly disconnected from the natural one.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Image Gallery Grid - Uniform squares */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {galleryImages.map((image, idx) => (
              <div 
                key={idx} 
                className="aspect-square cursor-pointer overflow-hidden"
                onClick={() => openLightbox(idx)}
              >
                <img 
                  src={image} 
                  alt={`Children of the Lea ${idx + 1}`}
                  className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          <button 
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-white/70 transition-colors"
          >
            <X size={32} />
          </button>
          <button 
            onClick={prevImage}
            className="absolute left-6 text-white hover:text-white/70 transition-colors"
          >
            <ChevronLeft size={48} />
          </button>
          <img 
            src={galleryImages[currentImageIndex]} 
            alt={`Gallery ${currentImageIndex + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
          <button 
            onClick={nextImage}
            className="absolute right-6 text-white hover:text-white/70 transition-colors"
          >
            <ChevronRight size={48} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {currentImageIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildrenOfLeaProject;
